import axios from "axios";

// API Configuration
// Use environment variable if set, otherwise use empty string for local API routes
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Helper function to get language from locale
// IMPORTANT: Always use explicit language - no auto-guessing
export function getLangFromLocale(locale) {
  if (!locale || typeof locale !== "string") {
    console.warn("getLangFromLocale: No locale provided, defaulting to 'en'");
    return "en"; // Default to English for consistency
  }

  const normalized = locale.toLowerCase().trim();

  if (normalized === "ar" || normalized.startsWith("ar")) {
    return "ar";
  }

  if (normalized === "en" || normalized.startsWith("en")) {
    return "en";
  }

  // If unclear, default to English (not Arabic) for consistency
  console.warn(`getLangFromLocale: Unrecognized locale '${locale}', defaulting to 'en'`);
  return "en";
}

// Helper function to get locale from pathname
// IMPORTANT: Always use explicit language from path - no auto-guessing
export function getLocaleFromPath(pathname) {
  if (!pathname || typeof pathname !== "string") {
    console.warn("getLocaleFromPath: No pathname provided, defaulting to 'en'");
    return "en";
  }

  if (pathname.startsWith("/ar-IQ")) return "ar";
  if (pathname.startsWith("/en-IQ")) return "en";

  // If unclear, default to English for consistency
  console.warn(`getLocaleFromPath: Unrecognized pathname '${pathname}', defaulting to 'en'`);
  return "en";
}

// Generic API fetch function
async function apiFetch(endpoint, options = {}) {
  try {
    const response = await apiClient(endpoint, options);
    return response.data;
  } catch (error) {
    // Handle network/connection errors
    if (!error.response) {
      console.warn(`API call failed for ${endpoint} (using fallback data):`, error.message);
      // Return null for network errors to allow fallbacks
      return null;
    }

    // Handle 404 - endpoint doesn't exist (common in development)
    if (error.response.status === 404) {
      console.warn(`API endpoint not found: ${endpoint} (using fallback data)`);
      // Return null instead of throwing for 404s to allow graceful fallbacks
      return null;
    }

    // Handle 500 - server error (e.g., missing database)
    if (error.response.status === 500) {
      console.warn(`API server error for ${endpoint} (using fallback data):`, error.response.data);
      // Return null for 500s to allow fallbacks
      return null;
    }

    // Extract server error message safely
    let serverMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.data?.msg ||
      error.message ||
      "Unknown server error";

    // If there are validation errors, include them
    if (error.response?.data?.validationErrors) {
      const validationErrors = error.response.data.validationErrors;
      serverMessage = {
        error: serverMessage,
        validationErrors: validationErrors,
      };
    }

    // Convert object errors (e.g. validation) into readable text
    if (typeof serverMessage === "object" && !serverMessage.validationErrors) {
      serverMessage = JSON.stringify(serverMessage);
    } else if (typeof serverMessage === "object") {
      // Keep validation errors as object for easier parsing
      serverMessage = JSON.stringify(serverMessage);
    }

    // Log for debugging
    console.error(`API Error for ${endpoint}:`, serverMessage);

    // Throw meaningful error
    throw new Error(serverMessage);
  }
}

// Home page API
// IMPORTANT: locale must be explicitly provided - no auto-guessing
export async function getPageHome(locale) {
  if (!locale) {
    throw new Error("getPageHome: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/home/${lang}`);
}

export async function updatePageHome(data) {
  return apiFetch("/api/dashboard/home", {
    method: "PUT",
    data: data,
  });
}

// Revalidate pages after updates
export async function revalidatePages(paths = ["/ar-IQ", "/en-IQ"]) {
  try {
    // Use the same origin (client-side) for revalidation
    const baseUrl = typeof window !== 'undefined'
      ? window.location.origin
      : process.env.NEXT_PUBLIC_APP_URL || '';

    const response = await fetch(`${baseUrl}/api/revalidate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        paths,
        secret: process.env.NEXT_PUBLIC_REVALIDATION_SECRET || "your-secret-key",
      }),
    });

    if (!response.ok) {
      throw new Error(`Revalidation failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Revalidation error:", error);
    // Don't throw - revalidation failure shouldn't break the save operation
    return { error: error.message };
  }
}

// About page API
// IMPORTANT: locale must be explicitly provided
export async function getAbout(locale) {
  if (!locale) {
    throw new Error("getAbout: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/about?lang=${lang}`);
}

export async function updateAbout(data) {
  return apiFetch("/api/about", {
    method: "PUT",
    data: data,
  });
}

// Shared Media API
export async function listMedia() {
  return apiFetch("/api/shared-media");
}

export async function uploadMedia(file, name, type = "image") {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", name);
  formData.append("type", type);

  return apiFetch("/api/shared-media", {
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}


export async function deleteMedia(id) {
  return apiFetch(`/api/shared-media?id=${id}`, {
    method: "DELETE",
  });
}

// Contact page API
// IMPORTANT: locale must be explicitly provided
export async function getContact(locale) {
  if (!locale) {
    throw new Error("getContact: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/contact?lang=${lang}`);
}

export async function updateContact(data) {
  return apiFetch("/api/contact", {
    method: "PUT",
    data: data,
  });
}

// News page API
// IMPORTANT: locale must be explicitly provided
export async function getNews(locale) {
  if (!locale) {
    throw new Error("getNews: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  // The server exposes the news/blog page under /api/blogs
  return apiFetch(`/api/blogs?lang=${lang}`);
}

export async function updateNews(data) {
  // The server expects updates to the blogs endpoint
  return apiFetch("/api/blogs", {
    method: "PUT",
    data: data,
  });
}

// Portfolio page API
// IMPORTANT: locale must be explicitly provided
export async function getPortfolio(locale) {
  if (!locale) {
    throw new Error("getPortfolio: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/portfolio?lang=${lang}`);
}

export async function updatePortfolio(data) {
  return apiFetch("/api/portfolio", {
    method: "PUT",
    data: data,
  });
}

// Individual project CRUD operations
export async function createProject(projectData) {
  return apiFetch("/api/portfolio/projects", {
    method: "POST",
    data: projectData,
  });
}

export async function updateProject(projectId, projectData) {
  return apiFetch(`/api/portfolio/projects/${projectId}`, {
    method: "PUT",
    data: projectData,
  });
}

export async function deleteProject(projectId) {
  return apiFetch(`/api/portfolio/projects/${projectId}`, {
    method: "DELETE",
  });
}

export async function getProject(projectId, locale) {
  if (!locale) {
    throw new Error("getProject: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/portfolio/projects/${projectId}?lang=${lang}`);
}

// Careers page API
// IMPORTANT: locale must be explicitly provided
export async function getCareers(locale) {
  if (!locale) {
    throw new Error("getCareers: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/careers?lang=${lang}`);
}

export async function updateCareers(data) {
  return apiFetch("/api/careers", {
    method: "PUT",
    data: data,
  });
}

// Services API
// IMPORTANT: locale must be explicitly provided
export async function getServices(locale, featured = false) {
  if (!locale) {
    throw new Error("getServices: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  const featuredParam = featured ? "&featured=true" : "";
  return apiFetch(`/api/services?lang=${lang}${featuredParam}`);
}

export async function getServiceBySlug(slug, locale) {
  if (!locale) {
    throw new Error("getServiceBySlug: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/services/${slug}?lang=${lang}`);
}

export async function updateServices(data) {
  return apiFetch("/api/services", {
    method: "PUT",
    data: data,
  });
}

// Services Page API (hero and serviceIntro)
// IMPORTANT: locale must be explicitly provided
export async function getServicesPage(locale) {
  if (!locale) {
    throw new Error("getServicesPage: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/services/page?lang=${lang}`);
}

// Individual service CRUD operations
export async function createService(serviceData) {
  return apiFetch("/api/services/service", {
    method: "POST",
    data: serviceData,
  });
}

export async function updateService(serviceId, serviceData) {
  return apiFetch(`/api/services/service/${serviceId}`, {
    method: "PUT",
    data: serviceData,
  });
}

export async function deleteService(serviceId) {
  return apiFetch(`/api/services/service/${serviceId}`, {
    method: "DELETE",
  });
}

export async function getService(serviceId, locale) {
  if (!locale) {
    throw new Error("getService: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/services/service/${serviceId}?lang=${lang}`);
}

// Blogs API
// IMPORTANT: locale must be explicitly provided
export async function getBlogs(locale, options = {}) {
  if (!locale) {
    throw new Error("getBlogs: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  const params = new URLSearchParams();
  params.append("lang", lang);

  if (options.featured) params.append("featured", "true");
  if (options.category) params.append("category", options.category);
  if (options.limit) params.append("limit", options.limit.toString());

  return apiFetch(`/api/blogs?${params.toString()}`);
}

export async function getBlogById(blogId, locale) {
  if (!locale) {
    throw new Error("getBlogById: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/blogs/${blogId}?lang=${lang}`);
}

export async function getBlogBySlug(slug, locale) {
  if (!locale) {
    throw new Error("getBlogBySlug: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  return apiFetch(`/api/blogs/slug/${slug}?lang=${lang}`);
}

export async function getFeaturedBlogs(locale, limit = 3) {
  if (!locale) {
    throw new Error("getFeaturedBlogs: locale parameter is required");
  }
  const lang = getLangFromLocale(locale);
  // Use the main blogs endpoint with query params to support the dev fallback
  // which exposes `/api/blogs` but not `/api/blogs/featured`.
  return getBlogs(locale, { featured: true, limit });
}

export async function createBlog(blogData) {
  return apiFetch("/api/blogs", {
    method: "POST",
    data: blogData,
  });
}

export async function updateBlog(blogId, blogData) {
  return apiFetch(`/api/blogs/${blogId}`, {
    method: "PUT",
    data: blogData,
  });
}

export async function deleteBlog(blogId) {
  return apiFetch(`/api/blogs/${blogId}`, {
    method: "DELETE",
  });
}

// Get related blogs (same category, excluding current blog)
export async function getRelatedBlogs(currentBlogId, locale, limit = 3) {
  if (!locale) {
    throw new Error("getRelatedBlogs: locale parameter is required");
  }

  try {
    // First, get the current blog to find its category
    const currentBlog = await getBlogById(currentBlogId, locale);
    if (!currentBlog) return [];

    // Get all blogs from the same category
    const lang = getLangFromLocale(locale);
    const params = new URLSearchParams();
    params.append("lang", lang);

    // If category exists, filter by it
    if (currentBlog.category) {
      params.append("category", currentBlog.category);
    }

    const blogs = await apiFetch(`/api/blogs?${params.toString()}`);

    // Filter out current blog and limit results
    return Array.isArray(blogs)
      ? blogs.filter((blog) => blog.id !== currentBlogId).slice(0, limit)
      : [];
  } catch (error) {
    console.error("Error fetching related blogs:", error);
    return [];
  }
}